import { createQueryKeys } from '@lukemorales/query-key-factory'
import AivatarClient from '../AivatarClient'
import { PaginationParams } from '../types'
import { getProjectList } from '../project'
import { getVideoExportList } from '../video'
import { getAudioExportList, getVoiceConversionList } from '../audio'

export async function getWorkspaceDetail(workspaceId: number) {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}`)
  return response.data
}

export async function getWorkspaceCredits(workspaceId: number) {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/credits`)

  return response.data as {
    current: number
    predict: number
    available: number
    offer: number
    used: number
  }
}

export async function getWorkspaceInvoiceList(workspaceId: number, params: PaginationParams) {
  const { page, size, sort } = params
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/invoices`, {
    params: {
      page,
      size,
      sort,
    },
  })
  return response.data
}

/**
 * 청구 내역 행 — 구독 결제(인보이스)와 크레딧 팩 구매(결제 완료)를 결제일 순 한 목록으로 (BE aivatar #339 · 화면 전달본 P4).
 * 구독 행의 subscription 은 종전 /invoices 행과 같은 모양이다.
 */
export type BillingHistoryItem = {
  type: 'SUBSCRIPTION' | 'CREDIT_PACK'
  invoiceId: number | null
  paymentOrderId: number | null
  itemName: string | null
  status: string
  /** 청구 총액(부가세 포함) */
  price: number
  currencyCode: string
  paidAt: string
  paymentNumber: string | null
  orderNumber: string | null
  /** 구독은 청구서, 크레딧 팩은 결제대행사(단건 상점) 영수증. 비어 있으면 링크 없이 행만 보인다 */
  slipUrl: string | null
  subscription: any | null
  /** 크레딧 팩일 때만 */
  grantedCredit: number | null
  /** 크레딧 팩일 때만. 그 결제 건의 소멸일(yyyy-MM-dd, 당일까지 유효) */
  expiresOn: string | null
}

export async function getWorkspaceBillingHistory(workspaceId: number, params: PaginationParams) {
  const { page, size, sort } = params
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/billing-history`, {
    params: {
      page,
      size,
      sort,
    },
  })
  return response.data as { content: BillingHistoryItem[]; totalElements: number }
}

export async function getWorkspaceVoiceList(workspaceId: number) {
  const response = await AivatarClient().get(`/workspaces/${workspaceId}/voices`)
  return response.data
}

export const workspaceQueries = createQueryKeys('workspace', {
  detail: (workspaceId: number) => ({
    queryKey: [workspaceId],
    queryFn: () => getWorkspaceDetail(workspaceId),
    contextQueries: {
      invoices: (params: Parameters<typeof getWorkspaceInvoiceList>[1]) => ({
        queryKey: [params],
        queryFn: () => getWorkspaceInvoiceList(workspaceId, params),
      }),
      billingHistory: (params: PaginationParams) => ({
        queryKey: [params],
        queryFn: () => getWorkspaceBillingHistory(workspaceId, params),
      }),
      credits: {
        queryKey: null,
        queryFn: () => getWorkspaceCredits(workspaceId),
      },
      projects: (params: PaginationParams) => ({
        queryKey: [params],
        queryFn: () => getProjectList(workspaceId, params),
      }),
      videoExports: (params: PaginationParams) => ({
        queryKey: [{ workspaceId, ...params }],
        queryFn: () => getVideoExportList(workspaceId, params),
      }),
      audioExports: (params: PaginationParams) => ({
        queryKey: [{ workspaceId, ...params }],
        queryFn: () => getAudioExportList(workspaceId, params),
      }),
      voices: {
        queryKey: null,
        queryFn: () => getWorkspaceVoiceList(workspaceId),
      },
      voiceConversions: {
        queryKey: null,
        queryFn: () => getVoiceConversionList(workspaceId),
      },
    },
  }),
})
